/**
 * Settings → Workspace: closing the workspace (M49 decision 7). The owner types the workspace's
 * name to close it; for 30 days everyone can still open, search, download and export but nothing
 * can be changed, then the workspace is deleted. Until then the owner can reopen it. Other members
 * see whether and when it closes.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { classifyFailure, closeWorkspace, encodeText, exportWorkspace, listWorkspaceExports, reopenWorkspace, workspaceExportFile, type WorkspaceClosure, type WorkspaceExport } from '@miraops/contracts';
import { download } from '../lib/download.ts';
import { Download } from '../ui/glyphs.tsx';
import { transport } from '../lib/session.ts';
import { useI18n } from '../lib/i18n.tsx';
import { failureText } from '../lib/failure-text.ts';
import { Banner, Button, Spinner } from '../ui/ui.tsx';
import { quietly } from '../lib/quietly.ts';

export function WorkspaceClosing({ workspaceId, workspaceName, closure, onChanged }: {
  workspaceId: string; workspaceName: string; closure: WorkspaceClosure | null; onChanged: () => void;
}) {
  const i18n = useI18n();
  const { t } = i18n;
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const request = useRef(crypto.randomUUID());
  async function act(work: () => Promise<unknown>) {
    setBusy(true); setError(null);
    try { await work(); request.current = crypto.randomUUID(); setTyped(''); onChanged(); }
    catch (e) {
      const token = String((e as { token?: string }).token ?? '');
      setError(token === 'confirmation_mismatch' ? t('closing.mismatch') : failureText(t, classifyFailure({ error: e, operation: 'write' })));
    } finally { setBusy(false); }
  }
  return (
    <div className="connected-apps account-data">
      {closure?.isOwner && <ExportWorkspace workspaceId={workspaceId} />}
      <section className="workspace-closing" aria-labelledby="closing-title">
        <h2 id="closing-title">{t('closing.title')}</h2>
        {error && <Banner tone="danger">{error}</Banner>}
        {!closure ? <Spinner label={t('state.loading')} /> : closure.status === 'closing' ? <>
          <p>{t('closing.isClosing', { date: closure.deleteAfter ? i18n.date.format(closure.deleteAfter) : '—' })}</p>
          {closure.isOwner && <div className="row-actions start">
            <Button variant="primary" disabled={busy} onClick={() => void act(() => reopenWorkspace(transport, workspaceId))}>{t('closing.reopen')}</Button></div>}
        </> : !closure.isOwner ? <p className="muted">{t('closing.ownerOnly')}</p> : <>
          <p className="muted">{t('closing.note')}</p>
          <form className="loc-form" onSubmit={e => { e.preventDefault(); if (typed === workspaceName && !busy) void act(() => closeWorkspace(transport, workspaceId, request.current, typed)); }}>
            <label className="field"><span>{t('closing.confirm', { name: workspaceName })}</span>
              <input className="field-input" autoComplete="off" spellCheck={false} value={typed} onChange={e => setTyped(e.target.value)} /></label>
            <div className="row-actions start"><Button variant="danger" type="submit" disabled={busy || typed !== workspaceName}>{t('closing.close')}</Button></div>
          </form>
        </>}
      </section>
    </div>
  );
}

/** The owner downloads the workspace's business records as one JSON file built on this device. */
function ExportWorkspace({ workspaceId }: { workspaceId: string }) {
  const i18n = useI18n();
  const { t } = i18n;
  const [history, setHistory] = useState<WorkspaceExport[] | null>(null);
  const [state, setState] = useState<{ status: 'running'; done: number; total: number } | { status: 'done'; file: string } | { status: 'failed'; reason: string } | null>(null);
  const load = useCallback(async (signal?: AbortSignal) => setHistory(await listWorkspaceExports(transport, workspaceId, signal)), [workspaceId]);
  useEffect(() => {
    const abort = new AbortController();
    load(abort.signal).catch(error => { if (!abort.signal.aborted) setState({ status: 'failed', reason: failureText(t, classifyFailure({ error, operation: 'read' })) }); });
    return () => abort.abort();
  }, [load]);
  async function run() {
    setState({ status: 'running', done: 0, total: 0 });
    try {
      const doc = await exportWorkspace(transport, workspaceId, crypto.randomUUID(), (done, total) => setState({ status: 'running', done, total }));
      const file = workspaceExportFile(doc, new Date());
      download(encodeText(file.text), file.name, 'application/json');
      setState({ status: 'done', file: file.name });
      await load().catch(quietly);
    } catch (error) {
      const f = classifyFailure({ error, operation: 'read' });
      setState({ status: 'failed', reason: f.kind === 'rate_limited' ? t('wexport.limit') : failureText(t, f) });
    }
  }
  return (
    <section className="workspace-export" aria-labelledby="wexport-title">
      <h2 id="wexport-title">{t('wexport.title')}</h2>
      <p className="muted">{t('wexport.note')}</p>
      <div className="row-actions start">
        <Button variant="primary" icon={<Download size={16} />} disabled={state?.status === 'running'} onClick={() => void run()}>
          {state?.status === 'running' ? t('wexport.running', { done: state.done, total: state.total }) : t('wexport.download')}</Button>
      </div>
      {state?.status === 'done' && <Banner tone="success">{t('data.saved', { file: state.file })}</Banner>}
      {state?.status === 'failed' && <Banner tone="danger">{state.reason}</Banner>}
      <h3>{t('data.history')}</h3>
      {!history ? <Spinner label={t('state.loading')} /> : history.length === 0 ? <p className="muted">{t('data.none')}</p> : (
        <ul className="account-data-list">{history.map(h => (
          <li key={h.id}><span>{i18n.dateTime.format(h.requestedAt)}</span><span className="muted">{h.requestedBy}</span></li>
        ))}</ul>
      )}
    </section>
  );
}
